/**
 * Pure, tested surface for the web sessions screen. Byte-equivalent twin of
 * `apps/mobile/src/account/sessionsScreenLogic.ts`. Duplicated on purpose:
 * there is no shared client-logic package.
 */

import { formatRelativeTime } from "@sportsmet/errors";

export type typeSessionRow = {
  sessionId: string;
  deviceLabel: string;
  client: "ios" | "android" | "web" | "unknown";
  createdAt: string;
  lastUsedAt?: string | null;
  isCurrent: boolean;
};

export const SESSIONS_HEADING = "Where you’re signed in";
export const THIS_DEVICE_LABEL = "This device";
export const SIGN_OUT_ROW_LABEL = "Sign out";
export const SIGN_OUT_ROW_CONFIRM_BODY =
  "That device will need to sign in again. Nothing else changes.";
export const SIGN_OUT_ROW_CONFIRM_LABEL = "Sign out that device";
export const SIGN_OUT_EVERYWHERE_LABEL = "Sign out everywhere else";
export const SIGN_OUT_EVERYWHERE_CONFIRM_TITLE =
  "Sign out of every other session?";
export const SIGN_OUT_EVERYWHERE_CONFIRM_BODY =
  "You’ll stay signed in here. Every other device will need to sign in again.";
/** Declared for parity with the mobile twin. The web screen does not render
 * this: envelope errors go through `showApiError`. */
export const SESSIONS_LOAD_ERROR =
  "We could not load your sessions just now. Go back and open this again in a moment.";

function activityMs(session: typeSessionRow): number {
  if (session.lastUsedAt == null || session.lastUsedAt === "") return 0;
  const parsed = Date.parse(session.lastUsedAt);
  return Number.isNaN(parsed) ? 0 : parsed;
}

/** Most-recently-active first. Never mutates the input array. */
export function sortSessionsForDisplay(
  sessions: readonly typeSessionRow[],
): typeSessionRow[] {
  return [...sessions].sort((left, right) => {
    const activityDelta = activityMs(right) - activityMs(left);
    if (activityDelta !== 0) return activityDelta;
    const createdDelta =
      Date.parse(right.createdAt) - Date.parse(left.createdAt);
    if (createdDelta !== 0) return createdDelta;
    if (left.sessionId < right.sessionId) return -1;
    if (left.sessionId > right.sessionId) return 1;
    return 0;
  });
}

/** The one line under the device label. */
export function sessionSecondaryLine(
  session: typeSessionRow,
  nowMs: number,
): string {
  if (session.isCurrent) return THIS_DEVICE_LABEL;
  const signedIn = formatRelativeTime(session.createdAt, nowMs);
  if (session.lastUsedAt) {
    return `Last active ${formatRelativeTime(session.lastUsedAt, nowMs)} · Signed in ${signedIn}`;
  }
  return `Not used since sign-in · Signed in ${signedIn}`;
}

/** False when `This device` is the only row — the bulk action is then disabled, not hidden. */
export function canSignOutEverywhereElse(
  sessions: readonly typeSessionRow[],
): boolean {
  return sessions.some((session) => !session.isCurrent);
}

export function signOutRowConfirmTitle(deviceLabel: string): string {
  return `Sign out of ${deviceLabel}?`;
}
